import React, { useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Card, Grid, Icon, Box, Tab, Tabs, CircularProgress, Pagination, Chip, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Backdrop } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArticleIcon from '@mui/icons-material/Article';
import PhotoIcon from '@mui/icons-material/Photo';
import MovieIcon from '@mui/icons-material/Movie';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";

function LessonsList() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 10;
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  // Fetch lessons data
  const fetchLessons = async () => {
    let url = "";
    if (tabValue === 0) {
      url = `${process.env.REACT_APP_API_URL}/lessons/list?page=${page}&limit=${limit}`;
    } else {
      if (!meData?.user_id) return null;
      url = `${process.env.REACT_APP_API_URL}/lessons/list/${meData.user_id}?page=${page}&limit=${limit}`;
    }

    const response = await axios.get(url, { withCredentials: true });
    return response.data;
  };

  const { data: lessonsData, isLoading: lessonsLoading, isError: lessonsError } = useQuery({
    queryKey: ["lessons", tabValue, page, meData?.user_id],
    queryFn: fetchLessons,
    enabled: tabValue === 0 || !!meData?.user_id,
    keepPreviousData: true,
  });

  const handlePostClick = () => {
    navigate("/lessons/add");
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1); // Reset page when switching tabs
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleMenuOpen = (event, lesson) => {
    setAnchorEl(event.currentTarget);
    setSelectedLesson(lesson);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Preserving selectedLesson for the dialog
  };

  const handleCloseAll = () => {
    setAnchorEl(null);
    setSelectedLesson(null);
    setDeleteDialogOpen(false);
  };

  const handleEdit = () => {
    navigate(`/lessons/edit/${selectedLesson.id}`);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!selectedLesson) return;
    setIsDeleting(true);
    setLoading(true);
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/lessons/delete/${selectedLesson.id}`, { withCredentials: true });
      setSnackbar({
        open: true,
        message: response?.data?.message || "Lesson deleted successfully!",
        severity: "success"
      });
      queryClient.invalidateQueries(["lessons"]);
    } catch (error) {
      console.error("DEBUG: Delete fail:", error.response || error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to delete lesson.",
        severity: "error"
      });
    } finally {
      setIsDeleting(false);
      setLoading(false);
      handleCloseAll();
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const renderEmptyState = () => (
    <Card sx={{ width: '100%', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', mb: 3 }}>
      <MDBox p={5} textAlign="center" display="flex" flexDirection="column" alignItems="center" width="100%">
        <Box sx={{ bgcolor: '#e8f5e9', p: 3, borderRadius: '50%', mb: 2 }}>
          <ArticleIcon sx={{ fontSize: 100, color: '#0b6750' }} />
        </Box>
        <MDTypography variant="h5" fontWeight="medium" gutterBottom>
          No lessons posted yet
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={3}>
          {tabValue === 0
            ? "Be the first to share your experience from the field!"
            : "You haven't posted any lessons yet."}
        </MDTypography>
        <MDButton
          variant="gradient"
          onClick={handlePostClick}
          sx={{
            px: 4,
            background: 'linear-gradient(195deg, #0b6750, #0b6750)',
            color: '#ffffff !important',
            '&:hover': {
              background: 'linear-gradient(195deg, #094d3d, #094d3d)',
            }
          }}
        >
          Start your first post
        </MDButton>
      </MDBox>
    </Card>
  );

  const renderLessons = () => {
    if (lessonsLoading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress color="info" />
        </MDBox>
      );
    }

    if (lessonsError || !lessonsData || lessonsData.data?.length === 0) {
      return renderEmptyState();
    }

    return (
      <Grid container spacing={0} sx={{ width: '100%', m: 0 }}>
        {lessonsData.data.map((lesson) => {
          // Parse videos safely
          let videoList = [];
          if (Array.isArray(lesson.video_url)) {
            videoList = lesson.video_url;
          } else if (typeof lesson.videos === "string") {
            try {
              videoList = JSON.parse(lesson.videos);
            } catch (e) {
              videoList = lesson.videos.split(",").filter((v) => v.trim() !== "");
            }
          } else if (Array.isArray(lesson.videos)) {
            videoList = lesson.videos;
          }

          const firstPhoto = lesson.photos?.[0];
          const firstVideo = videoList?.[0];
          const firstPdf = lesson.pdfs?.[0];

          const getPreview = () => {
            if (firstPhoto) return <img src={firstPhoto} alt="preview" className="media-zoom" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'all 0.5s ease' }} />;
            if (firstVideo) return <MovieIcon sx={{ fontSize: 110, color: '#0d47a1' }} />;
            if (firstPdf) return <PictureAsPdfIcon sx={{ fontSize: 110, color: '#b71c1c' }} />;

            // Fallback icons based on content presence even if first asset is missing
            if (lesson.photos?.length > 0) return <PhotoIcon sx={{ fontSize: 110, color: '#1b5e20' }} />;
            if (videoList?.length > 0) return <MovieIcon sx={{ fontSize: 110, color: '#0d47a1' }} />;
            if (lesson.pdfs?.length > 0) return <PictureAsPdfIcon sx={{ fontSize: 110, color: '#b71c1c' }} />;

            return <ArticleIcon sx={{ fontSize: 110, color: '#0b6750' }} />;
          };

          const getFallbackBg = () => {
            if (lesson.photos?.length > 0) return '#81c784'; // Vibrant Green
            if (videoList?.length > 0) return '#64b5f6'; // Vibrant Blue
            if (lesson.pdfs?.length > 0) return '#e57373';   // Vibrant Red
            return '#c8e6c9'; // Default lessons background
          };

          return (
            <Grid item xs={12} key={lesson.id} sx={{ width: '100%', p: 0, mb: 4, display: 'flex', justifyContent: 'center' }}>
              <Card sx={{
                minHeight: { xs: 'auto', md: 350 },
                height: 'auto',
                width: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                "&:hover": {
                  boxShadow: "0 20px 45px rgba(0,0,0,0.18)",
                  transform: "translateY(-5px)",
                  "& .media-zoom": {
                    transform: "scale(1.1)"
                  }
                }
              }} onClick={() => navigate(`/lessons/details/${lesson.id}`)}>
                {/* Left Side: Media Preview - 40% Width */}
                <Box sx={{
                  width: { xs: '100%', md: '40%' },
                  height: { xs: 200, md: '100%' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: getFallbackBg(),
                  borderRight: { md: '1px solid #eee' },
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {getPreview()}
                </Box>

                {/* Right Side: Content - 60% Width */}
                <Box sx={{
                  width: { xs: '100%', md: '60%' },
                  p: { xs: 2, sm: 3, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  minHeight: { xs: 'auto', md: '100%' }
                }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <MDTypography variant="h5" fontWeight="bold" color="dark" mb={1} sx={{
                        pr: 5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {lesson.title || "Lesson from the field"}
                      </MDTypography>

                      {/* Three dots only for author AND my posts tab */}
                      {tabValue === 1 && (
                        <IconButton
                          sx={{ position: 'absolute', top: { xs: 8, md: 15 }, right: { xs: 8, md: 15 } }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, lesson);
                          }}
                        >
                          <MoreVertIcon fontSize="medium" />
                        </IconButton>
                      )}
                    </Box>

                    <Box sx={{
                      overflow: 'hidden',
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis'
                    }}>
                      <MDTypography variant="body1" color="text" sx={{ fontSize: '1.05rem', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: lesson.content }} />
                    </Box>

                    {/* Media Chips */}
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                      {lesson.photos?.length > 0 && (
                        <Chip label={`${lesson.photos.length} Photos`} icon={<PhotoIcon style={{ color: 'white' }} />} sx={{ height: 28, p: 0.5, bgcolor: '#4caf50', color: 'white', fontWeight: 'bold', '& .MuiChip-icon': { color: 'white' } }} />
                      )}
                      {videoList?.length > 0 && (
                        <Chip label={`${videoList.length} Videos`} icon={<MovieIcon style={{ color: 'white' }} />} sx={{ height: 28, p: 0.5, bgcolor: '#2196f3', color: 'white', fontWeight: 'bold', '& .MuiChip-icon': { color: 'white' } }} />
                      )}
                      {lesson.pdfs?.length > 0 && (
                        <Chip label={`${lesson.pdfs.length} PDFs`} icon={<PictureAsPdfIcon style={{ color: 'white' }} />} sx={{ height: 28, p: 0.5, bgcolor: '#f44336', color: 'white', fontWeight: 'bold', '& .MuiChip-icon': { color: 'white' } }} />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Box display="flex" alignItems="center" mb={1.5}>
                      <Box>
                        <MDTypography variant="h6" fontWeight="bold" color="dark" display="block">
                          {lesson.user_name}
                        </MDTypography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AccessTimeIcon sx={{ fontSize: 16, color: '#7b809a' }} />
                          <MDTypography variant="button" color="text" sx={{ fontSize: '0.85rem' }}>
                            {moment(lesson.created_at).format("DD MMM YYYY")}
                          </MDTypography>
                        </Box>
                      </Box>
                    </Box>

                    <MDButton
                      fullWidth
                      variant="gradient"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/lessons/details/${lesson.id}`);
                      }}
                      sx={{
                        background: 'linear-gradient(195deg, #0b6750, #0b6750)',
                        color: '#ffffff !important',
                        fontSize: { xs: '0.875.rem', md: '1rem' },
                        fontWeight: 'bold',
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: 'uppercase',
                        '&:hover': {
                          background: 'linear-gradient(195deg, #094d3d, #094d3d)',
                          transform: 'scale(1.01)'
                        }
                      }}
                    >
                      Read Full Post <ArrowForwardIcon sx={{ ml: 1, fontSize: 20 }} />
                    </MDButton>
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <DashboardLayout>
      <MDBox py={2} sx={{ width: '100% !important', maxWidth: '100% !important' }}>
        <MDBox mb={2} sx={{ width: '100%' }} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h4" fontWeight="bold" textTransform="capitalize" color="dark">
            Lessons from the Field
          </MDTypography>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handlePostClick}
            startIcon={<Icon>add</Icon>}
          >
            Post Here
          </MDButton>
        </MDBox>

        <MDBox mb={3}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="lessons-tabs"
            textColor="inherit"
            sx={{
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                fontWeight: 'bold',
                borderRadius: 2,
                transition: 'all 0.3s',
                '&.Mui-selected': {
                  background: 'linear-gradient(195deg, #0b6750, #0b6750)',
                  color: '#ffffff !important',
                }
              }
            }}
          >
            <Tab label="All Posts" />
            <Tab label="My Posts" />
          </Tabs>
        </MDBox>

        <MDBox mt={3} mb={3} sx={{ width: '100%', pb: 5 }}>
          {renderLessons()}
        </MDBox>

        {lessonsData && lessonsData.totalCount > limit && (
          <MDBox display="flex" justifyContent="center" mt={3} mb={3}>
            <Pagination
              count={Math.ceil(lessonsData.totalCount / limit)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  margin: '0 5px'
                },
                '& .Mui-selected': {
                  background: 'linear-gradient(195deg, #0b6750, #0b6750) !important',
                  color: 'white !important',
                  border: 'none'
                }
              }}
            />
          </MDBox>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              width: 150,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderRadius: 2
            }
          }}
        >
          <MenuItem onClick={handleEdit} sx={{ gap: 1.5, py: 1 }}>
            <Icon color="info" fontSize="small">edit</Icon>
            <MDTypography variant="button" fontWeight="medium">Edit</MDTypography>
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', gap: 1.5, py: 1 }}>
            <Icon color="error" fontSize="small">delete</Icon>
            <MDTypography variant="button" fontWeight="medium" color="error">Delete</MDTypography>
          </MenuItem>
        </Menu>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
          <DialogTitle sx={{ textAlign: 'center' }}>
            <Icon color="error" sx={{ fontSize: 50, mb: 1 }}>delete</Icon>
            <MDTypography variant="h5" fontWeight="bold">Delete Post?</MDTypography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <MDTypography variant="body2" color="text">
              Are you sure you want to remove this lesson? <br />
              This action is permanent and cannot be undone.
            </MDTypography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
            <MDButton onClick={() => setDeleteDialogOpen(false)} color="light" sx={{ minWidth: 100 }}>
              No, Cancel
            </MDButton>
            <MDButton onClick={confirmDelete} color="error" variant="gradient" disabled={isDeleting} sx={{ minWidth: 100 }}>
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Informational Cards Row - Only show if no data/loading/error */}
        {(lessonsError || !lessonsData || lessonsData.data?.length === 0) && !lessonsLoading && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'nowrap' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card sx={{ height: "100%", minHeight: 200, p: 3, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                <Box sx={{ p: 2, bgcolor: "#e8f5e9", borderRadius: "50%", mb: 2 }}>
                  <Icon fontSize="large" sx={{ color: "#4caf50" }}>lightbulb</Icon>
                </Box>
                <MDTypography variant="h6" fontWeight="medium">Share Insights</MDTypography>
                <MDTypography variant="body2" color="text" mt={1}>
                  Share your valuable learnings and observations from the field to help others grow.
                </MDTypography>
              </Card>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card sx={{ height: "100%", minHeight: 200, p: 3, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                <Box sx={{ p: 2, bgcolor: "#fff3e0", borderRadius: "50%", mb: 2 }}>
                  <Icon fontSize="large" sx={{ color: "#ff9800" }}>collections</Icon>
                </Box>
                <MDTypography variant="h6" fontWeight="medium">Upload Media</MDTypography>
                <MDTypography variant="body2" color="text" mt={1}>
                  Enhance your stories with photos, videos, and documents for better engagement.
                </MDTypography>
              </Card>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card sx={{ height: "100%", minHeight: 200, p: 3, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                <Box sx={{ p: 2, bgcolor: "#e3f2fd", borderRadius: "50%", mb: 2 }}>
                  <Icon fontSize="large" sx={{ color: "#2196f3" }}>groups</Icon>
                </Box>
                <MDTypography variant="h6" fontWeight="medium">Engage Community</MDTypography>
                <MDTypography variant="body2" color="text" mt={1}>
                  Connect with fellow members and foster a culture of shared knowledge.
                </MDTypography>
              </Card>
            </Box>
          </Box>
        )}
      </MDBox>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', color: 'white !important' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default LessonsList;
